import useAPI, {
  useAPIMutation,
  // Plain async fetch helper (not a hook despite the name) — alias so the
  // rules-of-hooks lint doesn't flag the on-demand call inside useCallback.
  useAPIHelper as fetchAPIData
} from "@/services/api/hook";
import {
  SETTINGS_ADMIN_PERMISSION,
  useAuth,
  useHasPermission
} from "@/services/auth/AuthContext";
import { ExtendedUseQueryResult } from "@/services/api/types";
import {
  APIFieldsResponse,
  APIFieldItem,
  APIFieldsSectionsResponse,
  APIFieldDisplayTypeTargets,
  CreateLinkedDataInfo
} from "./types";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  FieldSectionPage,
  getPageFieldSections
} from "@/utils/entityFieldsUtils/fieldSections";
import { camelizeFieldKey } from "@/utils/fieldKeys";

export const useCanAccessAdminSettings = (): boolean =>
  useHasPermission(SETTINGS_ADMIN_PERMISSION);

export enum FieldTypeLevel {
  ALL = "all",
  STANDARD = "standard",
  ADDITIONAL = "additional"
}

type CreateFieldItem = Omit<
  APIFieldItem,
  "id" | "fieldKey" | "order" | "linkedDataInfo"
>;

export type CreateFieldRequest = {
  [K in keyof CreateFieldItem]?: CreateFieldItem[K] | null;
} & {
  linkedDataInfo?: CreateLinkedDataInfo | null;
};

export const useEntityFields = (
  entityType: EntityType
): ExtendedUseQueryResult<APIFieldsResponse> => {
  const result = useAPI("settings/entityType/fields", {
    pathParams: { entityType: entityTypeToEntityName[entityType] }
  });

  const filteredData = useMemo(() => {
    return result.data
      ? {
          ...result.data,
          fields:
            result.data.fields?.filter(
              (field: APIFieldItem) => !field.isDeleted
            ) || []
        }
      : result.data;
  }, [result.data]);

  return useMemo(
    () => ({
      ...result,
      data: filteredData
    }),
    [result, filteredData]
  );
};

export const useActiveFieldKeys = (
  entityType: EntityType
): {
  activeFieldKeys: Set<string>;
  detailViewFieldKeys: Set<string>;
  allFieldKeys: Set<string>;
  isLoaded: boolean;
} => {
  const result = useAPI("settings/entityType/fields", {
    pathParams: { entityType: entityTypeToEntityName[entityType] }
  });
  const isLoaded = result.data !== undefined;
  const allFieldKeys = useMemo(
    () =>
      new Set(
        ((result.data as APIFieldsResponse | undefined)?.fields || []).map(
          (field: APIFieldItem) => camelizeFieldKey(field.fieldKey)
        )
      ),
    [result.data]
  );
  const activeFieldKeys = useMemo(
    () =>
      new Set(
        ((result.data as APIFieldsResponse | undefined)?.fields || [])
          .filter((field: APIFieldItem) => !field.isDeleted)
          .map((field: APIFieldItem) => camelizeFieldKey(field.fieldKey))
      ),
    [result.data]
  );
  const detailViewFieldKeys = useMemo(
    () =>
      new Set(
        ((result.data as APIFieldsResponse | undefined)?.fields || [])
          .filter(
            (field: APIFieldItem) =>
              !field.isDeleted && field.showInDetailedView
          )
          .map((field: APIFieldItem) => camelizeFieldKey(field.fieldKey))
      ),
    [result.data]
  );
  return { activeFieldKeys, detailViewFieldKeys, allFieldKeys, isLoaded };
};

export const useFieldVisibility = (entityType: EntityType) => {
  const { activeFieldKeys, allFieldKeys, isLoaded } =
    useActiveFieldKeys(entityType);

  const isFieldVisible = useMemo(
    () => (key: string) => !allFieldKeys.has(key) || activeFieldKeys.has(key),
    [allFieldKeys, activeFieldKeys]
  );

  const filterVisibleColumnKeys = useMemo(
    () => (columnKeys: string[]) => columnKeys.filter(isFieldVisible),
    [isFieldVisible]
  );

  return {
    isFieldVisible,
    filterVisibleColumnKeys,
    activeFieldKeys,
    allFieldKeys,
    isLoaded
  };
};

export const useEntityFieldsWithAdditionals = (
  entityType: EntityType,
  options?: { fieldTypeLevel?: FieldTypeLevel }
): ExtendedUseQueryResult<APIFieldsResponse> => {
  const result = useAPI("settings/entityType/fields/additionals", {
    pathParams: { entityType: entityTypeToEntityName[entityType] },
    queryParams: {
      ...(options?.fieldTypeLevel && {
        field_type_level: options.fieldTypeLevel
      })
    }
  });

  const filteredData = useMemo(() => {
    return result.data
      ? {
          ...result.data,
          fields:
            result.data.fields?.filter(
              (field: APIFieldItem) => !field.isDeleted
            ) || []
        }
      : result.data;
  }, [result.data]);

  return useMemo(
    () => ({
      ...result,
      data: filteredData
    }),
    [result, filteredData]
  );
};

export const useFieldDisplayTypeTargets = (
  entityType: EntityType,
  fieldId: string | undefined,
  enabled: boolean
): ExtendedUseQueryResult<APIFieldDisplayTypeTargets> => {
  return useAPI(
    "settings/entityType/fields/displayTypeTargets",
    {
      pathParams: {
        entityType: entityTypeToEntityName[entityType],
        fieldId: fieldId ?? ""
      }
    },
    { enabled: enabled && !!fieldId }
  );
};

export const useFieldsSections = (
  entityType: EntityType,
  options?: { enabled?: boolean }
): ExtendedUseQueryResult<APIFieldsSectionsResponse> => {
  const result = useAPI(
    "settings/entityType/fields_sections",
    { pathParams: { entityType: entityTypeToEntityName[entityType] } },
    options?.enabled === false ? { enabled: false } : undefined
  );

  return result;
};

// Tab-visibility gate for the Additional Info tab: true only when this
// specific entity type has at least one additional field assigned to a
// section that still exists. Distinct from the account-wide
// "additional_info_tab_enabled" setting, which just turns the feature on —
// without this, an entity type with zero additional fields configured would
// still render an empty tab whenever any other entity type has some.
export const useHasAdditionalFields = (entityType: EntityType): boolean => {
  const { data: fieldsResponse } = useEntityFieldsWithAdditionals(
    entityType,
    { fieldTypeLevel: FieldTypeLevel.ADDITIONAL }
  );
  const { data: fieldsSectionsResponse } = useFieldsSections(entityType);

  return useMemo(() => {
    const additionalSections = getPageFieldSections(
      fieldsSectionsResponse?.fieldsSections || [],
      FieldSectionPage.ADDITIONAL
    );
    const sectionIds = new Set(
      additionalSections.map((section) => String(section.id))
    );
    return (fieldsResponse?.fields || []).some(
      (field) => field.section && sectionIds.has(String(field.section))
    );
  }, [fieldsResponse?.fields, fieldsSectionsResponse?.fieldsSections]);
};

export const useEntityFieldItems = (entityType: EntityType) => {
  const apiResult = useEntityFields(entityType);

  const transformedData =
    apiResult.data?.fields?.map((apiField: APIFieldItem) => ({
      id: apiField.id,
      fieldLabel: apiField.label,
      fieldType: apiField.displayType,
      dataSource: apiField.dataSource
    })) || [];

  return {
    ...apiResult,
    data: transformedData,
    fields: transformedData
  };
};

export const useUpdateEntityFieldsOrder = (entityType: EntityType) => {
  return useAPIMutation<{ data: string[] }, any>(
    "settings/entityType/fields/order",
    {
      method: "PUT",
      pathParams: { entityType: entityTypeToEntityName[entityType] }
    }
  );
};

export const useCreateEntityField = (entityType: EntityType) => {
  const queryClient = useQueryClient();

  return useAPIMutation<CreateFieldRequest, APIFieldItem>(
    "settings/entityType/fields/create",
    {
      method: "POST",
      pathParams: { entityType: entityTypeToEntityName[entityType] }
    },
    () => {
      // Invalidate the fields query to refresh the table
      queryClient.invalidateQueries({
        queryKey: [
          "settings/entityType/fields",
          { pathParams: { entityType: entityTypeToEntityName[entityType] } }
        ]
      });
    }
  );
};

export const useDeleteEntityField = (entityType: EntityType) => {
  const queryClient = useQueryClient();

  return useAPIMutation<
    { pathParams: { entityType: string; fieldId: string } },
    { success: boolean; message?: string }
  >(
    "settings/entityType/fields/delete",
    {
      method: "DELETE"
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: [
          "settings/entityType/fields",
          { pathParams: { entityType: entityTypeToEntityName[entityType] } }
        ]
      });
    }
  );
};

export type UpdateFieldRequest = Partial<
  Omit<APIFieldItem, "id" | "fieldKey" | "order">
> & {
  id: string;
};

export const useUpdateEntityField = (entityType: EntityType) => {
  const queryClient = useQueryClient();

  return useAPIMutation<
    {
      data: UpdateFieldRequest;
      pathParams: { entityType: string; fieldId: string };
    },
    APIFieldItem
  >(
    "settings/entityType/fields/update",
    {
      method: "PUT"
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: [
          "settings/entityType/fields",
          { pathParams: { entityType: entityTypeToEntityName[entityType] } }
        ]
      });
      // A saved change may alter the field's type or populated state, which
      // drives the allowed conversion targets — drop all cached targets.
      queryClient.invalidateQueries({
        queryKey: ["settings/entityType/fields/displayTypeTargets"]
      });
    }
  );
};

export interface EditableSystemFieldOption {
  // number for integer-PK lookups (industries), string for generated string PKs
  // (sub-industries).
  id?: number | string;
  name: string;
  // Visibility flag; omit to leave the stored value unchanged. Only sent for
  // system-aware lookups when the configurable-system-fields flag is on.
  isHidden?: boolean;
}

export interface EditableSystemFieldOptionsUpdate {
  options: EditableSystemFieldOption[];
  // Name of the option to use as the field default, or NONE_OPTION_VALUE
  // ("none") when no default is set.
  defaultValue?: string;
}

// Hide/show is no longer a per-click call — visibility is staged in local state
// and persisted through the bulk save below (isHidden per item).

// Generic bulk-update for a parent-scoped lookup field; routes come from the registry.
export const useLookupBulkUpdate = (
  { bulkRoute, readRoute }: { bulkRoute?: string; readRoute?: string },
  entityType: EntityType
) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    // Refetch the parent-scoped values so the panel reflects the save.
    if (readRoute) {
      queryClient.invalidateQueries({ queryKey: [readRoute] });
    }
    queryClient.invalidateQueries({
      queryKey: [
        "settings/entityType/fields",
        { pathParams: { entityType: entityTypeToEntityName[entityType] } }
      ]
    });
    // Refresh the owning entity's queries so a renamed value isn't shown stale.
    queryClient.invalidateQueries({
      queryKey: [entityTypeToEntityName[entityType]]
    });
  };
  return useAPIMutation<
    { pathParams: Record<string, string>; data: EditableSystemFieldOption[] },
    unknown
  >(
    // Fallback keeps the hook valid when instantiated for a non-scoped field
    // (never fired in that case).
    bulkRoute ?? "settings/subIndustries/bulkUpdate",
    { method: "PUT" },
    invalidate,
    invalidate
  );
};

// On-demand usage lookup for the hide/rename modals (how many records use a value).
export const useLookupValueUsage = () => {
  const { token, tenantId, tokenExpiry } = useAuth();
  return useCallback(
    async (
      routeKey: "settings/industries/usage" | "settings/subIndustries/usage",
      pathParams: Record<string, string>
    ): Promise<number> => {
      const data = await fetchAPIData({ token, tenantId, tokenExpiry }, [
        routeKey,
        { pathParams }
      ]);
      return data?.count ?? 0;
    },
    [token, tenantId, tokenExpiry]
  );
};

export const useUpdateEditableSystemFieldOptions = (entityType: EntityType) => {
  const queryClient = useQueryClient();

  return useAPIMutation<
    {
      data: EditableSystemFieldOption[] | EditableSystemFieldOptionsUpdate;
      pathParams: { entityType: string; fieldKey: string };
    },
    { success: boolean }
  >(
    "settings/entityType/fields/bulkUpdate",
    {
      method: "PUT"
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: [
          "settings/entityType/fields",
          { pathParams: { entityType: entityTypeToEntityName[entityType] } }
        ]
      });
      // Industries render from the dedicated settings/industries read (IndustryOptionsPanel),
      // so refetch it after a save or the reopened drawer shows the pre-save list.
      queryClient.invalidateQueries({ queryKey: ["settings/industries"] });
      // A rename changes how the value renders on records — refresh the owning
      // entity's list/detail queries so they don't show the pre-save name until refresh.
      queryClient.invalidateQueries({
        queryKey: [entityTypeToEntityName[entityType]]
      });
    }
  );
};
